import axios from "axios";
import { toast } from "sonner";
import { reportError } from "./errorReporter";
import { getAuthToken, isImpersonating, exitImpersonation } from "./impersonation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests. In a "view as client" tab this returns the
// per-tab impersonation token; otherwise the manager's normal login token.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - only redirect on actual auth failures, not all 401s
let isRedirecting = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Report 5xx and network errors to the centralized error log so admins can
    // triage them without digging through container logs. Skip the reporter
    // endpoint itself to avoid recursion if it's the one failing.
    try {
      const status = error.response?.status;
      const reqUrl = error.config?.url || "";
      const isReporterEndpoint = reqUrl.includes("/errors/frontend") || reqUrl.includes("/errors/");
      const isNetworkError = !error.response;
      const is5xx = status >= 500 && status < 600;
      if (!isReporterEndpoint && (is5xx || isNetworkError)) {
        const method = (error.config?.method || "GET").toUpperCase();
        const fullPath = `${error.config?.baseURL || ""}${reqUrl}`;
        reportError({
          message: isNetworkError
            ? `Network error: ${method} ${reqUrl}`
            : `${status} from ${method} ${reqUrl}: ${error.response?.data?.detail || error.message}`,
          stack: error.stack || null,
          extra: {
            request_url: fullPath,
            request_method: method,
            response_status: status || null,
            response_data: error.response?.data ? JSON.stringify(error.response.data).slice(0, 1000) : null,
          },
        });
      }
    } catch (_) {
      // never let the reporter break the original error flow
    }

    // Read-only "view as client": the backend blocks writes with 403. Surface a
    // toast so the manager understands why nothing happened, and swallow it.
    if (error.response?.status === 403 && isImpersonating()) {
      const detail = error.response?.data?.detail || "";
      if (/read-only|перегляд як клієнт|дія недоступна/i.test(detail)) {
        toast.info("Перегляд як клієнт — дія недоступна (read-only).");
      }
    }

    // In an impersonation tab, a 401 means the short-lived preview token expired.
    // Exit impersonation and bounce THIS tab — but never touch the manager's own
    // localStorage login (it's shared across tabs; clearing it would log them out
    // everywhere).
    if (error.response?.status === 401 && isImpersonating()) {
      exitImpersonation();
      if (!isRedirecting) {
        isRedirecting = true;
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        setTimeout(() => { isRedirecting = false; }, 1000);
      }
      return Promise.reject(error);
    }

    // Only handle 401 for auth-related endpoints or when there's a clear auth failure
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      // Only clear token and redirect for main auth endpoints
      // Skip for public endpoints that may optionally check auth
      const isAuthEndpoint = url.includes("/auth/me") || url.includes("/auth/login");
      const isPublicEndpoint = url.includes("/currency-rates/current");

      if (isAuthEndpoint && !isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem("token");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        // Reset after a short delay to allow redirect
        setTimeout(() => { isRedirecting = false; }, 1000);
      } else if (!isPublicEndpoint && !isRedirecting) {
        // For other protected endpoints, check if token exists
        const token = localStorage.getItem("token");
        if (!token) {
          isRedirecting = true;
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          setTimeout(() => { isRedirecting = false; }, 1000);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const projectsApi = {
  getAll: () => api.get("/projects"),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post("/projects", data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  addTeamMember: (projectId, userId) => api.post(`/projects/${projectId}/team?user_id=${userId}`),
  addArtist: (projectId, userId) => api.post(`/projects/${projectId}/artists?user_id=${userId}`),
  updateBudget: (projectId, budget) => api.put(`/projects/${projectId}/budget?budget=${budget}`),
};

// Unified Projects API (merged sales + PM projects)
export const unifiedProjectsApi = {
  getAll: (params) => api.get("/unified-projects", { params }),
  getOne: (id) => api.get(`/unified-projects/${id}`),
  create: (data) => api.post("/unified-projects", data),
  update: (id, data) => api.put(`/unified-projects/${id}`, data),
  delete: (id) => api.delete(`/unified-projects/${id}`),
  addTeamMember: (projectId, userId) => api.post(`/unified-projects/${projectId}/team?user_id=${userId}`),
  addArtist: (projectId, artistId) => api.post(`/unified-projects/${projectId}/artists?artist_id=${artistId}`),
  updateBudget: (projectId, budget) => api.put(`/unified-projects/${projectId}/budget?budget=${budget}`),
  migrate: () => api.post("/unified-projects/migrate"),
  // PM Pool
  getPmPool: () => api.get("/pm-pool"),
  takeFromPool: (projectId) => api.post(`/pm-pool/${projectId}/take`),
};

export const tasksApi = {
  getAll: (params) => api.get("/tasks", { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post("/tasks", data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  // Legacy file upload (kept for backward compatibility)
  uploadReference: (taskId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/tasks/${taskId}/reference`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // New: Add reference link
  addReferenceLink: (taskId, data) => api.post(`/tasks/${taskId}/reference-link`, data),
  // Delete reference (link or file)
  deleteReference: (taskId, referenceId) => api.delete(`/tasks/${taskId}/reference/${referenceId}`),
  // Legacy file upload version
  uploadVersion: (taskId, file, comment) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("comment", comment || "");
    return api.post(`/tasks/${taskId}/version`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // New: Submit version link (artist submits URL)
  submitVersionLink: (taskId, data) => api.post(`/tasks/${taskId}/version-link`, data),
  // Internal review (Art Director / PM Lead / PM)
  internalReviewVersion: (taskId, versionId, data) => 
    api.post(`/tasks/${taskId}/versions/${versionId}/internal-review`, data),
  // Legacy approve (for backward compatibility)
  approveVersion: (taskId, versionId) => api.post(`/tasks/${taskId}/versions/${versionId}/approve`),
  // Client revision request
  requestClientRevision: (taskId, data) => api.post(`/tasks/${taskId}/client-revision`, data),
  // Legacy revision request
  requestRevision: (taskId, comment) => api.post(`/tasks/${taskId}/revision?comment=${encodeURIComponent(comment)}`),
  updatePayment: (taskId, status) => api.put(`/tasks/${taskId}/payment`, { status }),
};

export const usersApi = {
  getAll: (role, roles) => api.get("/users", { params: { role, roles } }),
  getOne: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  delete: (id) => api.delete(`/users/${id}`),
};

export const commentsApi = {
  getAll: (taskId) => api.get("/comments", { params: { task_id: taskId } }),
  create: (data) => api.post("/comments", data),
};

export const notificationsApi = {
  getAll: () => api.get("/notifications"),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put("/notifications/read-all"),
};

export const statsApi = {
  getDashboard: () => api.get("/stats/dashboard"),
  getProjectsHealth: () => api.get("/stats/projects-health"),
  getArtistPerformance: () => api.get("/stats/artist-performance"),
  getFinancialSummary: () => api.get("/stats/financial-summary"),
  getOwnerDashboard: (period = "all", month = null) => api.get("/stats/owner-dashboard", { params: { period, month } }),
  getDashboardV2Overview: (period = null, monthsBack = 6) => api.get("/dashboard-v2/overview", { params: { ...(period ? { period } : {}), months_back: monthsBack } }),
};

export const paymentsApi = {
  getAll: () => api.get("/payments"),
};

export const employeesApi = {
  getAll: (isActive) => api.get("/employees", { params: { is_active: isActive } }),
  getOrgStructure: () => api.get("/org-structure"),
  autofillDepartments: (overwrite = false) => api.post(`/org-structure/autofill-departments?overwrite=${overwrite}`),
  listDepartments: () => api.get("/departments"),
  createDepartment: (data) => api.post("/departments", data),
  updateDepartment: (id, data) => api.put(`/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/departments/${id}`),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post("/employees", data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  createAccount: (id, data) => api.post(`/employees/${id}/create-account`, data),
  hasAccount: (id) => api.get(`/employees/${id}/has-account`),
  resetPassword: (id, data) => api.post(`/employees/${id}/reset-password`, data),
  updateRole: (id, data) => api.post(`/employees/${id}/update-role`, data),
  setRoleplayAccess: (id, data) => api.post(`/employees/${id}/roleplay-access`, data),
  setPortfolioAccess: (id, data) => api.post(`/employees/${id}/portfolio-access`, data),
  createPortalUser: (data) => api.post("/employees/create-portal-user", data),
  resendInvite: (id) => api.post(`/admin/resend-client-invite/${id}`),
  getEmailLogs: (userId) => api.get(`/admin/email-logs?user_id=${userId}`),
  listInvites: () => api.get("/admin/invites"),
  regenerateInvite: (userId) => api.post(`/admin/invites/regenerate/${userId}`),
};

export const salaryPaymentsApi = {
  getAll: (params) => api.get("/salary-payments", { params }),
  create: (data) => api.post("/salary-payments", data),
  update: (id, data) => api.put(`/salary-payments/${id}`, data),
  delete: (id) => api.delete(`/salary-payments/${id}`),
  getMonthlySummary: () => api.get("/salary-payments/monthly-summary"),
  generateMonth: (month) => api.post(`/salary-payments/generate-month?month=${month}`),
};

export const ownerWithdrawalsApi = {
  getAll: (params) => api.get("/owner-withdrawals", { params }),
  create: (data) => api.post("/owner-withdrawals", data),
  delete: (id) => api.delete(`/owner-withdrawals/${id}`),
};

export const taskPaymentsApi = {
  getAll: (params) => api.get("/task-payments", { params }),
  create: (data) => api.post("/task-payments", data),
  update: (id, status) => api.put(`/task-payments/${id}?status=${status}`),
  delete: (id) => api.delete(`/task-payments/${id}`),
  getPnlForecast: (startMonth, monthsCount = 12) => 
    api.get("/stats/pnl-forecast", { params: { start_month: startMonth, months_count: monthsCount } }),
};

export const currencyApi = {
  getCurrent: () => api.get("/currency-rates/current"),
  getHistory: (limit = 30) => api.get("/currency-rates", { params: { limit } }),
  setRate: (data) => api.post("/currency-rates", data),
  fetchToday: () => api.get("/currency-rates/fetch-today"),
  getSalarySummaryUsd: (month) => api.get("/stats/salary-summary-usd", { params: { month } }),
};

// Sales Projects API
export const salesProjectsApi = {
  getAll: (month, salesManagerId) => api.get("/sales-projects", { 
    params: { month, sales_manager_id: salesManagerId } 
  }),
  create: (data) => api.post("/sales-projects", data),
  update: (id, data) => api.put(`/sales-projects/${id}`, data),
  delete: (id) => api.delete(`/sales-projects/${id}`),
  approve: (id) => api.post(`/sales-projects/${id}/approve`),
  bulkImport: (projects) => api.post("/sales-projects/bulk-import", projects),
};

// Sales Services (Payments) API
export const salesServicesApi = {
  getAll: (params) => api.get("/sales-services", { params }),
  getOne: (id) => api.get(`/sales-services/${id}`),
  create: (data) => api.post("/sales-services", data),
  update: (id, data) => api.put(`/sales-services/${id}`, data),
  delete: (id) => api.delete(`/sales-services/${id}`),
  changeStatus: (id, data) => api.put(`/sales-services/${id}/status`, data),
  changePaidDate: (id, data) => api.put(`/sales-services/${id}/paid-date`, data),
  split: (data) => api.post("/sales-services/split", data),
  bulkClassify: (data) => api.post("/sales-services/bulk-classify", data),
};

// Sales Payments API (Fact)
export const salesPaymentsApi = {
  getAll: (month, salesProjectId) => api.get("/sales-payments", { 
    params: { month, sales_project_id: salesProjectId } 
  }),
  create: (data) => api.post("/sales-payments", data),
  delete: (id) => api.delete(`/sales-payments/${id}`),
};

// Sales Dashboard API
export const salesDashboardApi = {
  getStats: (month) => api.get("/sales-stats/dashboard", { params: { month } }),
};

// Other Expenses API
export const otherExpensesApi = {
  getAll: (params) => api.get("/other-expenses", { params }),
  getOne: (id) => api.get(`/other-expenses/${id}`),
  create: (data) => api.post("/other-expenses", data),
  update: (id, data) => api.put(`/other-expenses/${id}`, data),
  changeStatus: (id, data) => api.put(`/other-expenses/${id}/change-status`, data),
  delete: (id) => api.delete(`/other-expenses/${id}`),
  getStats: (expenseType) => api.get("/other-expenses/stats", { params: { expense_type: expenseType } }),
  importExcel: () => api.post("/other-expenses/import-excel"),
  deleteImport: (importId) => api.delete(`/other-expenses/import/${importId}`),
};

// CRM Income API
export const crmApi = {
  // Clients — `clientType` filters by client_types tag ("crm"|"support"|"any")
  getClients: (activeOnly = true, clientType = "crm") =>
    api.get("/crm/clients", { params: { active_only: activeOnly, client_type: clientType } }),
  getClient: (id) => api.get(`/crm/clients/${id}`),
  createClient: (data) => api.post("/crm/clients", data),
  updateClient: (id, data) => api.put(`/crm/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/crm/clients/${id}`),
  reorderClients: (order) => api.post("/crm/clients/reorder", order),

  // Dashboard
  getDashboard: () => api.get("/crm/dashboard"),

  // Payments — pass `params.category` ("crm"|"support") to scope by billing category
  getPayments: (params) => api.get("/crm/payments", { params }),
  createPayment: (data) => api.post("/crm/payments", data),
  updatePayment: (id, data) => api.put(`/crm/payments/${id}`, data),
  deletePayment: (id) => api.delete(`/crm/payments/${id}`),
  reorderPayments: (order) => api.post("/crm/payments/reorder", order),
  
  // Stats & Import
  getStats: (year = 2026) => api.get("/crm/stats", { params: { year } }),
  getExtraStats: (year = 2026) => api.get("/crm/extra-stats", { params: { year } }),
  importCsv: () => api.post("/crm/import-csv"),
  deleteImport: (importId) => api.delete(`/crm/import/${importId}`),
  generatePlan: (year) => api.post(`/crm/generate-plan/${year}`),
  generateMonthPayments: (month, category = "crm") =>
    api.post(`/crm/generate-month/${month}`, null, { params: { category } }),
  
  // Employee bonus
  getEmployeeBonus: (employeeId, year = 2026) => api.get(`/crm/employee-bonus/${employeeId}`, { params: { year } }),
};

// Checklist Templates API (Flexible Checklist System)
export const checklistApi = {
  // Templates
  getTemplates: (params) => api.get("/checklist-templates", { params }),
  getTemplate: (id) => api.get(`/checklist-templates/${id}`),
  createTemplate: (data) => api.post("/checklist-templates", data),
  updateTemplate: (id, data) => api.put(`/checklist-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/checklist-templates/${id}`),
  
  // Instances (filled checklists)
  getInstances: (params) => api.get("/checklist-instances", { params }),
  createInstance: (data) => api.post("/checklist-instances", data),
  updateInstance: (id, data) => api.put(`/checklist-instances/${id}`, data),
  updateField: (instanceId, fieldId, data) => api.put(`/checklist-instances/${instanceId}/fields/${fieldId}`, data),
  deleteInstance: (id) => api.delete(`/checklist-instances/${id}`),
};

// Risk Prevention API
export const riskPreventionApi = {
  // Service Stages
  getServiceStages: () => api.get("/service-stages"),
  createServiceStage: (data) => api.post("/service-stages", data),
  
  // Legacy Project Checklist (to be replaced by checklistApi)
  getChecklist: (projectId) => api.get(`/projects/${projectId}/checklist`),
  updateChecklistItem: (projectId, itemId, data) => api.put(`/projects/${projectId}/checklist/${itemId}`, data),
  
  // Input Files
  getInputFiles: (projectId) => api.get(`/projects/${projectId}/input-files`),
  uploadInputFile: (projectId, fileType, file) => {
    const formData = new FormData();
    formData.append("file_type", fileType);
    formData.append("file", file);
    return api.post(`/projects/${projectId}/input-files`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  
  // Scope Snapshots
  getScopeSnapshots: (projectId) => api.get(`/projects/${projectId}/scope-snapshots`),
  createScopeSnapshot: (projectId) => api.post(`/projects/${projectId}/scope-snapshot`),
  
  // Change Requests
  getChangeRequests: (projectId, serviceId = null) => {
    const params = serviceId ? { service_id: serviceId } : {};
    return api.get(`/projects/${projectId}/change-requests`, { params });
  },
  createChangeRequest: (projectId, data) => api.post(`/projects/${projectId}/change-requests`, data),
  updateChangeRequest: (projectId, requestId, data) => api.put(`/projects/${projectId}/change-requests/${requestId}`, data),
  applyChangeRequestsToService: (projectId, serviceId) => api.post(`/projects/${projectId}/services/${serviceId}/apply-change-requests`),

  // Project Start
  startProject: (projectId) => api.post(`/projects/${projectId}/start`),
  
  // Artist Load
  getArtistLoadSettings: () => api.get("/artist/load-settings"),
  updateArtistLoadSettings: (data) => api.put("/artist/load-settings", data),
  getArtistLoad: (artistId) => api.get(`/artists/${artistId}/load`),
  
  // Task Revisions
  getTaskRevisions: (taskId) => api.get(`/tasks/${taskId}/revisions`),
  addTaskRevision: (taskId, data) => api.post(`/tasks/${taskId}/revisions`, data),
};

// FOP Settings API
export const fopSettingsApi = {
  getAll: () => api.get("/settings/fops"),
  create: (data) => api.post("/settings/fops", data),
  update: (id, data) => api.put(`/settings/fops/${id}`, data),
  delete: (id) => api.delete(`/settings/fops/${id}`),
  reorder: (order) => api.post("/settings/fops/reorder", order),
  uploadSignature: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/settings/fops/${id}/signature`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getSignatureUrl: (id) => `${BACKEND_URL}/api/settings/fops/${id}/signature`,
  deleteSignature: (id) => api.delete(`/settings/fops/${id}/signature`),
};

// Sales Clients API — primary CRUD for db.clients across all client_types
export const salesClientsApi = {
  // Pass clientType="any" to fetch every client regardless of tag, or
  // "sales|crm|support" to filter. Default keeps legacy sales-only behaviour.
  getAll: (clientType = "sales") =>
    api.get("/sales-clients", { params: { client_type: clientType } }),
  get: (id) => api.get(`/sales-clients/${id}`),
  getOne: (id) => api.get(`/sales-clients/${id}`),
  create: (data) => api.post("/sales-clients", data),
  update: (id, data) => api.put(`/sales-clients/${id}`, data),
  delete: (id) => api.delete(`/sales-clients/${id}`),
  getAvailableUsers: (id, search = "") => api.get(`/sales-clients/${id}/available-users?search=${encodeURIComponent(search)}`),
};

// Timeline Templates API
export const timelineApi = {
  // Templates
  getTemplates: (serviceType) => api.get(`/timeline-templates${serviceType ? `?service_type=${serviceType}` : ''}`),
  getTemplate: (id) => api.get(`/timeline-templates/${id}`),
  createTemplate: (data) => api.post("/timeline-templates", data),
  updateTemplate: (id, data) => api.put(`/timeline-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/timeline-templates/${id}`),

  // Task timeline progress
  getTaskTimeline: (taskId) => api.get(`/tasks/${taskId}/timeline`),
  updateTaskStage: (taskId, data) => api.put(`/tasks/${taskId}/timeline/stage`, data),
};

// Task Milestones (PM sub-milestones inside a task) — DEPRECATED 2026-05-11.
// Milestones are now derived from timeline stage_history, not manually entered.
// Endpoints remain available for any legacy data, but new Gantt doesn't call them.
export const taskMilestonesApi = {
  list: (taskId) => api.get(`/tasks/${taskId}/milestones`),
  listForProject: (projectId) => api.get(`/projects/${projectId}/task-milestones`),
  create: (taskId, data) => api.post(`/tasks/${taskId}/milestones`, data),
  update: (taskId, milestoneId, data) => api.put(`/tasks/${taskId}/milestones/${milestoneId}`, data),
  delete: (taskId, milestoneId) => api.delete(`/tasks/${taskId}/milestones/${milestoneId}`),
};

// Bulk snapshot of every task's timeline state in a project. Single source of truth
// for Gantt rendering, project badges, progress views. Backend resolves task.status
// → stage_key → service-type template → {name, color}, plus normalizes stage_history.
export const projectTimelineSnapshotApi = {
  get: (projectId) => api.get(`/projects/${projectId}/task-timeline-snapshot`),
};

// PM-planned milestones: target dates for canonical phases. Outline flags on Gantt.
export const plannedMilestonesApi = {
  set: (taskId, milestones) => api.put(`/tasks/${taskId}/planned-milestones`, { milestones }),
};

// Project weekly health check (Friday switchers on Gantt)
export const projectWeekStatusApi = {
  list: (projectId) => api.get(`/projects/${projectId}/week-statuses`),
  upsert: (projectId, data) => api.put(`/projects/${projectId}/week-statuses`, data),
  clear: (projectId, weekEnding) => api.delete(`/projects/${projectId}/week-statuses/${encodeURIComponent(weekEnding)}`),
};

export const getFileUrl = (fileId) => `${BACKEND_URL}/api/files/${fileId}`;

// ============ WAITING MANAGEMENT & SLA ============

export const waitingApi = {
  // Dashboard for admins/managers
  getDashboard: (params) => api.get("/waiting-dashboard", { params }),
  // Personal waiting items (for all roles)
  getMyItems: () => api.get("/my-waiting-items"),
  // SLA settings
  getSlaSettings: () => api.get("/settings/sla"),
  updateSlaSettings: (data) => api.put("/settings/sla", data),
};

// Client Requests API (extended with SLA)
export const clientRequestsApi = {
  getAll: (params) => api.get("/client-requests", { params }),
  getOne: (id) => api.get(`/client-requests/${id}`),
  create: (data) => api.post("/client-requests", data),
  respond: (id, data) => api.post(`/client-requests/${id}/respond`, data),
  delete: (id) => api.delete(`/client-requests/${id}`),
  getTemplates: () => api.get("/checklist-templates"),
};

